import { useEffect, useMemo, type ReactElement } from 'react';
import { Alert, Box, Group, Loader } from '@mantine/core';
import { AlertTriangle, LineChart } from 'lucide-react';
import PageHeaderComponent from '@/design/PageHeaderComponent';
import PageLevelFilterRow from '@/shared/surfaces/PageLevelFilterRow';
import SideRailPageLayout from '@/shared/surfaces/SideRailPageLayout';
import SpendCostRail from './SpendCostRail';
import { useViewModel } from '@/shared/viewmodels/useViewModel';
import { useGlobalFilters } from '@/features/global-filters/useGlobalFilters';
import SurfaceAssistScope, { SurfaceAssistToolbar } from '@/shared/surfaces/assist/SurfaceAssistScope';
import { useSurface } from '@/shared/surfaces/useSurface';
import TopicNotFound from '@/shared/surfaces/TopicNotFound';
import { LicenseCostPageViewModel } from '@/features/licenses/LicenseCostPageViewModel';
import CostTrendChart from '@/features/licenses/CostTrendChart';

/** Spend › Cost › Spend Trend: the per-provider cost trend over the selected range. */
export default function SpendCostTrendPage(): ReactElement {
    const vm = useViewModel(LicenseCostPageViewModel);
    const surface = useSurface('token-usage');
    const { snapshot } = useGlobalFilters();
    const filterKey = useMemo(() => JSON.stringify(snapshot), [snapshot]);

    useEffect(() => {
        void vm.reload();
    }, [vm, filterKey]);

    if (surface === undefined) return <TopicNotFound />;

    return (
        <SurfaceAssistScope surface={surface}>
        <Box pb="xl">
            <PageLevelFilterRow assist={<SurfaceAssistToolbar surface={surface} />} />

            <PageHeaderComponent icon={LineChart} eyebrow="Cost" title="Spend Trend" subtitle="How hard-dollar provider cost moves over the selected range." />

            <Box mt="lg">
                {vm.loading ?
                    <Group justify="center" py={64}>
                        <Loader size="sm" />
                    </Group>
                : vm.loadError ?
                    <Alert color="red" icon={<AlertTriangle size={16} />} title="Could not load cost data">
                        {vm.loadError}
                    </Alert>
                :   <SideRailPageLayout rail={<SpendCostRail vm={vm} scopeKey={filterKey} />}>
                        <CostTrendChart trend={vm.trend} series={vm.trendSeries} currency={vm.currency} />
                    </SideRailPageLayout>
                }
            </Box>
        </Box>
        </SurfaceAssistScope>
    );
}
